import { useState, useEffect, useRef, SetStateAction, Dispatch, useContext } from 'react';
import { useSearchParams } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckedState } from '@radix-ui/react-checkbox';
import { useForm } from 'react-hook-form';
import { Address } from 'shared/types/account/Address';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import Loader from '@/components/customercenter-ui/common/loader/loader';
import {
  CART_STATE_FROZEN,
  DEFAULT_COUNTRY,
  PAYLOAD_QUERY,
  RESPONSE_SUCCESS_MESSAGE,
} from '@/components/customercenter-ui/components/customer-detail/constants';
import { AddressSchema } from '@/components/customercenter-ui/components/customer-detail/schema';
import { transformShippingAddress } from '@/components/customercenter-ui/components/customer-detail/utils';
import { CustomFloatingSelect } from '@/components/customercenter-ui/components/ui/customSelect/floatingSelect';
import { useToast } from '@/components/customercenter-ui/components/ui/use-toast';
import Step1 from '@/components/customercenter-ui/icons/step1';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import SubtractCircle from '@/components/customercenter-ui/svgs/subtractCircle';
import { PaymentMethods, useCheckout } from '@/context/checkoutProvider';
import { CreditSummaryContext } from '@/context/creditSummaryProvider';
import FloatingTextArea from '@/customercenter-ui/common/customForm/floatingTextArea';
import AddCircle from '@/customercenter-ui/icons/addCircle';
import { Button } from '@/customercenter-ui/ui/button';
import { useAccount, useCart } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { GlobalPageTexts } from '@/helpers/i18n';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { getCookieValue, getSelectedDivision, TranslateText } from '@/helpers/utils/siteUtils';
import {
  AddressPayload,
  CustomerAddressPayload,
  UpdateOrderPayload,
} from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { COOKIE_KEYS, DEFAULT_SITE_LANG, MAX_PAGE_SIZE } from '../../../../constants/constant';
import EditAddressModal from '../../../customercenter-ui/components/modals/customerDetailAction/editAddress';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { checkDeliveryAddress, findMatchingAddress } from '../../utils/addressUtil';
import { maskPhoneNumber, maskPostalCode } from '../../utils/formatUtils';
import ShippingForm from '../common/shipping/ShippingForm';
import ShippingMethod from '../common/shipping/ShippingMethodRadioButton';

interface GuestShippingProps {
  data: any;
  onPaymentPage: boolean;
  cartData?: any;
  shippingMethods?: any;
  isOrderDetails?: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
  setOrderShippingAddress?: any;
  orderShippingAddress?: any;
  setIsEditShippingLoading?: any;
  registerReset?: (fn: () => void) => void;
}
const GuestShipping: React.FC<GuestShippingProps> = ({
  cartData,
  shippingMethods,
  isOrderDetails,
  setOpen,
  setOrderShippingAddress,
  orderShippingAddress,
  setIsEditShippingLoading,
  registerReset,
}) => {
  const {
    shippingMethod,
    setShippingAddressValue,
    markStepAsComplete,
    goToCheckoutStep,
    shippingAddress: selectedShippingAddress,
    setGiftMessage: setGiftMessageContext,
    checkoutExtraDetails,
    setNotificationMsg,
    setError,
    setDeliveryStatus,
    setLastEnteredShippingAddress,
    setSignatureRequired,
    setCustomerDetailsResponse,
    customerDetailsResponse: customerAccountResponse,
    customerAccountAddresses,
    setCustomerAccountAddresses,
    selectedPaymentMethod,
    setSelectedPaymentMethods,
    setContextCart,
  } = useCheckout();

  const { setCustomerData, setCustomerDetailsResponse: setCreditSummaryDetailsResponse } =
    useContext(CreditSummaryContext);
  const { updateCart } = useCart(false);
  const { toast } = useToast();
  const projectSettings: ProjectSettings = useProjSettings();
  const params = useSearchParams();
  const { addNewAddress, getCustomerAddresses, getCustomerDetails, updateOrder } = useAccount();
  const [shippingAddress, setShippingAddress] = useState<any>(null);
  const [previousShippingAddress, setPreviousShippingAddress] = useState<any>(null);
  const [billingAddress, setBillingAddress] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [giftMessage, setGiftMessage] = useState(checkoutExtraDetails.giftMessage);
  const [showGiftMessage, setShowGiftMessage] = useState(false);
  const verifyAddressRef = useRef<(data: any) => Promise<void> | null>(() => Promise.resolve());
  const [addressType, setAddressType] = useState('savedAddress');
  const [shippingSameAsBilling, setShippingSameAsBilling] = useState<CheckedState>(true);
  const [openEditAddressModal, setOpenEditAddressModal] = useState(false);
  const [customerAddresses, setCustomerAddresses] = useState<any>([]);
  const [isCartHold, setIscartHold] = useState(false);
  const [isSoldAccount, setIsSoldAccount] = useState(false);
  const [openAddAddressModal, setOpenAddAddressModal] = useState(false);
  const Texts = TranslateText(GlobalPageTexts, DEFAULT_SITE_LANG);
  const allowedPaymentMethods = JSON.parse(projectSettings?.allowedPaymentMethods ?? '');
  const divisionCode = getSelectedDivision() ?? 'ZB';
  const allowedMethodsForDivision: any = allowedPaymentMethods?.[divisionCode];

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
    formState,
    getValues,
    reset,
    control,
    trigger,
  } = useForm<any>({
    resolver: zodResolver(AddressSchema),
    defaultValues: selectedShippingAddress || {},
  });

  const resetState = () => {
    setShippingSameAsBilling(false);
    setShippingAddress(previousShippingAddress);
    setCurrentShippingAddress(previousShippingAddress);
  };

  useEffect(() => {
    if (isOrderDetails) {
      registerReset?.(resetState);
    }
  }, [previousShippingAddress]);

  useEffect(() => {
    if (shippingAddress?.addressId !== billingAddress?.addressId) {
      setShippingSameAsBilling(false);
    }
  }, []);

  const onSubmit = async (data: any) => {
    try {
      const addrData: any = addressType === 'newAddress' ? { ...data } : { ...shippingAddress };
      let updatedAddress = addrData;

      if (isOrderDetails && orderShippingAddress) {
        const ordershippingAddress = findMatchingAddress([orderShippingAddress], data);
        if (ordershippingAddress) {
          setOpen?.(false);
          return;
        }
      }

      const matchingAddress = findMatchingAddress(customerAccountAddresses, data);
      const addNewAdd = isOrderDetails && shippingSameAsBilling && !matchingAddress;
      if (matchingAddress) {
        updatedAddress = matchingAddress;
      }
      setIsEditShippingLoading?.(true);
      if (addressType === 'newAddress' || addNewAdd) {
        const strippedPhoneNumber = data?.phone?.match(/\d/g)?.join('');
        const reFormattedPhoneNumber =
          strippedPhoneNumber?.length === 10
            ? `${strippedPhoneNumber.slice(0, 3)}-${strippedPhoneNumber.slice(3, 6)}-${strippedPhoneNumber.slice(
                6,
                10,
              )}`
            : '';
        const payload = {
          query: {
            omsCustomerId: params.get(PAYLOAD_QUERY.OMS_CUSTOMER_ID) ?? cartData?.omsCustomerId,
            divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
          },
          data: {
            LocationInfo: {
              isResidential: true,
              line1: data?.streetName ?? '',
              line2: data?.additionalAddressInfo ?? '',
              city: data?.city ?? '',
              state: data?.state ?? '',
              zip: data?.postalCode ?? '',
              country: DEFAULT_COUNTRY,
              phoneNumber: reFormattedPhoneNumber,
            },
            PersonInfo: {
              firstName: data?.firstName ?? '',
              lastName: data?.lastName ?? '',
              companyName: data?.companyName ?? '',
            },
          },
        } as AddressPayload;
        await addNewAddress(payload)
          .then(async () => {
            if (!addNewAdd) {
              SuccessNotification(toast, { description: 'Address Added', action: <SuccessIcon /> });
            }
            const getAddressPayload = {
              query: {
                omsCustomerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID) ?? cartData?.omsCustomerId,
                pageSize: MAX_PAGE_SIZE,
                divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
              },
            } as CustomerAddressPayload;
            const customerAddressesResponse = await getCustomerAddresses(getAddressPayload);
            if (customerAddressesResponse?.code === RESPONSE_SUCCESS_MESSAGE) {
              const transformedAddress = transformShippingAddress(customerAddressesResponse?.RecipientAddresses || []);
              setCustomerAccountAddresses(transformedAddress);
              if (isOrderDetails) {
                updatedAddress =
                  addressType === 'newAddress' ? addrData : transformedAddress?.[transformedAddress?.length - 1];
                handleAddressTypeChange('savedAddress');
                setShippingSameAsBilling(false);
              }
            }
          })
          .catch(() => {
            ErrorNotification(toast, {
              description: 'Error Adding Address',
              action: <ErrorIcon className="h-5 w-5" />,
            });
          });
      }
      if (!addrData || Object.keys(addrData).length === 0) {
        ErrorNotification(toast, {
          description: 'Please select an address',
          action: <ErrorIcon className="h-5 w-5" />,
        });
        return;
      }
      try {
        setCurrentShippingAddress(addrData);
        await updateShippingAddress(updatedAddress);
        if (!isOrderDetails) {
          goToCheckoutStep(2);
        }
      } catch (error) {
        setError(true);
      }
      setIsEditShippingLoading?.(false);
    } catch (error) {
      setError(true);
      setNotificationMsg(Texts?.shippingAddressUpdateError);
      setIsEditShippingLoading?.(false);
    }
  };

  const handleGiftValue = (e: any) => {
    setGiftMessageContext(e.target.value);
    setGiftMessage(e.target.value);
  };

  const handleShowGiftMessage = () => {
    setShowGiftMessage((prev) => !prev);
  };

  function mapCustomerDataToBillingAddress(customerData: any) {
    const customerInfo = customerData.Customer.CustomerInfo;
    const billingAddress = customerData.Customer.BillingAddress.LocationInfo;
    const primaryMobileNumber = customerData?.Customer?.DivisionCustomers?.[0]?.PhoneRecords?.filter(
      (phoneData: any) => phoneData?.HeaderInfo?.isPrimary,
    );
    return {
      fullName: `${customerInfo.firstName} ${customerInfo.lastName}`,
      addressId: 'B-ADD',
      firstName: customerInfo.firstName,
      lastName: customerInfo.lastName,
      streetName: billingAddress.line1,
      city: billingAddress.city,
      country: billingAddress.country,
      state: billingAddress.state,
      postalCode: billingAddress.zip,
      additionalAddressInfo: billingAddress.line2 || '',
      phone: primaryMobileNumber?.[0]?.PhoneInfo?.phoneNumber ?? '',
    };
  }

  const callVerifyAddress = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (verifyAddressRef.current) {
      verifyAddressRef.current(getValues());
    }
    handleSubmit(onSubmit)(event);
  };

  const updateShippingAddress = async (shippingAddress: Address) => {
    const { deliveryStatus, deliveryErrorMsg } = checkDeliveryAddress(shippingAddress, cartData);

    if (isOrderDetails) {
      const payload = {
        query: {
          orderId: params.get(PAYLOAD_QUERY.ORDER_ID),
          divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        },
        data: {
          Recipients: [
            {
              Address: {
                HeaderInfo: {
                  addressId: shippingAddress?.addressId,
                },
              },
            },
          ],
        },
      } as UpdateOrderPayload;
      await updateOrder(payload).then((res) => {
        if (res && res?.code === RESPONSE_SUCCESS_MESSAGE) {
          SuccessNotification(toast, { description: 'Address Added', action: <SuccessIcon /> });
          setOpen?.(false);
          setOrderShippingAddress(shippingAddress);
          setShippingAddress(previousShippingAddress);
          setShippingSameAsBilling(false);
          setCurrentShippingAddress(previousShippingAddress);
        }
      });
    } else {
      if (!deliveryStatus) {
        setDeliveryStatus(false);
        setError(true);
        setNotificationMsg(deliveryErrorMsg ?? '');
        window.scroll(0, 0);
        throw new Error('Invalid delivery address');
      } else {
        setDeliveryStatus(true);
        setIsLoading(true);
        await updateCart({
          shipping: { ...shippingAddress, country: 'US' },
          giftNote: giftMessage,
          shouldUpdateShippingAmount: false,
          shippingMethod: { id: shippingMethod ?? '' },
        })
          .then(async (updatedCart) => {
            if (updatedCart) {
              setContextCart(updatedCart);
            }
            setIsLoading(false);
            goToCheckoutStep(2);
            markStepAsComplete(1);
          })
          .catch((error) => {
            setIsLoading(false);
            throw error;
          });
      }
    }
  };

  const handleAddressTypeChange = (value: string) => {
    setAddressType(value);
  };

  const setCurrentShippingAddress = (address: any) => {
    setShippingAddressValue(address);
    setShippingAddress(address);
    reset(address);
  };

  useEffect(() => {
    const getAndUpdate = async () => {
      if (addressType === 'savedAddress') {
        const localAddresses = await getLocalAddressesData();
        if (localAddresses?.length > 0) {
          await onAddressChange('B-ADD', localAddresses);
        }
        if (Object.keys(selectedShippingAddress ?? {}).length > 0) {
          if (selectedShippingAddress?.addressId != undefined) {
            await onAddressChange(selectedShippingAddress?.addressId, localAddresses);
          } else {
            const lastSavedAddress = localAddresses?.[localAddresses?.length - 1];
            await onAddressChange(lastSavedAddress?.addressId, localAddresses);
          }
        }
      }
    };
    getAndUpdate();
    if (addressType === 'newAddress') {
      const fieldsMap = Object.fromEntries(Object.keys(control?._fields || {})?.map((field) => [field, '']));
      reset(fieldsMap);
    }
  }, [addressType]);

  useEffect(() => {
    if (projectSettings?.allowedPaymentMethods) {
      const allowedPaymentMethods = JSON.parse(projectSettings?.allowedPaymentMethods ?? '');
      const allowedMethodsForDivision: any = allowedPaymentMethods?.[divisionCode];
      if (
        !allowedMethodsForDivision?.includes(PaymentMethods.CREDIT) &&
        selectedPaymentMethod?.toUpperCase() === PaymentMethods.CREDIT
      ) {
        setSelectedPaymentMethods(PaymentMethods.CARD);
      }
    }
  }, [projectSettings, selectedPaymentMethod]);

  const getLocalAddressesData = async () => {
    setIsLoading(true);
    let addresses: any[] = [];
    try {
      let responseBillingAddress = null;
      let customerDetailsResponse = null;
      const customerId = params.get(PAYLOAD_QUERY.CUSTOMER_ID) ?? cartData?.omsCustomerId;
      if (
        customerAccountResponse?.code === RESPONSE_SUCCESS_MESSAGE &&
        customerAccountResponse?.Customer?.customerId === customerId
      ) {
        customerDetailsResponse = customerAccountResponse;
        responseBillingAddress = mapCustomerDataToBillingAddress(customerDetailsResponse);
        setShippingAddressValue(responseBillingAddress);
        setPreviousShippingAddress(responseBillingAddress);
        setBillingAddress(responseBillingAddress);
        setCustomerData(customerDetailsResponse?.Customer);
        setCreditSummaryDetailsResponse(customerDetailsResponse);
        const isSoldAccount = projectSettings?.assetSaleEnabled
          ? customerDetailsResponse?.Customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.isSoldAccount ??
            false
          : false;
        setIsSoldAccount(isSoldAccount);
        if (
          projectSettings?.assetSaleEnabled &&
          isSoldAccount &&
          (!selectedPaymentMethod || selectedPaymentMethod?.toUpperCase() === PaymentMethods.CREDIT)
        ) {
          setSelectedPaymentMethods(PaymentMethods.CARD);
        }
      } else {
        customerDetailsResponse = await getCustomerDetails({
          query: {
            customerId: customerId,
            omsCustomerId: params.get(PAYLOAD_QUERY.OMS_CUSTOMER_ID) ?? cartData?.omsCustomerId,
            divisionCode: divisionCode,
            durationInMonths: params.get(PAYLOAD_QUERY.durationInMonths) ?? '',
            creditAccountId: '',
            mailingRecordId: '',
            idName: 'customerId',
          },
        });
        if (customerDetailsResponse?.code === RESPONSE_SUCCESS_MESSAGE) {
          responseBillingAddress = mapCustomerDataToBillingAddress(customerDetailsResponse);
          setShippingAddressValue(responseBillingAddress);
          setBillingAddress(responseBillingAddress);
          setPreviousShippingAddress(responseBillingAddress);
          setCustomerDetailsResponse(customerDetailsResponse);
          setCustomerData(customerDetailsResponse?.Customer);
          setCreditSummaryDetailsResponse(customerDetailsResponse);
          const isSoldAccount = projectSettings?.assetSaleEnabled
            ? customerDetailsResponse?.Customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.isSoldAccount ??
              false
            : false;
          setIsSoldAccount(isSoldAccount);
          if (
            projectSettings?.assetSaleEnabled &&
            isSoldAccount &&
            (!selectedPaymentMethod || selectedPaymentMethod?.toUpperCase() === PaymentMethods.CREDIT)
          ) {
            setSelectedPaymentMethods(PaymentMethods.CARD);
          }
        }
      }
      if (customerAccountAddresses && customerAccountAddresses?.length > 0) {
        addresses = [...customerAccountAddresses];
        if (addresses?.length === 0 || shippingSameAsBilling) {
          addresses = [...customerAccountAddresses, { ...responseBillingAddress, country: 'US' }];
          const billingAddressWithCountry = { ...responseBillingAddress, country: 'US' };
          setCurrentShippingAddress(billingAddressWithCountry);
          setShippingSameAsBilling(true);
        }
        setCustomerAddresses(addresses);
      } else {
        const payload = {
          query: {
            omsCustomerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID) ?? cartData?.omsCustomerId,
            pageSize: MAX_PAGE_SIZE,
            divisionCode: divisionCode,
          },
        } as CustomerAddressPayload;
        const customerAddressesResponse = await getCustomerAddresses(payload);
        if (customerAddressesResponse?.code === RESPONSE_SUCCESS_MESSAGE) {
          const transformedAddress = transformShippingAddress(customerAddressesResponse?.RecipientAddresses || []);
          setCustomerAccountAddresses(transformedAddress);
          addresses = [...transformedAddress];
          if (addresses?.length === 0 || shippingSameAsBilling) {
            addresses = [...transformedAddress, { ...responseBillingAddress, country: 'US' }];
            const billingAddressWithCountry = { ...responseBillingAddress, country: 'US' };
            setCurrentShippingAddress(billingAddressWithCountry);
            setShippingSameAsBilling(true);
          }
          setCustomerAddresses(addresses);
        }
      }
    } catch (err) {
      console.error(err);
    }
    setIsLoading(false);
    return addresses;
  };

  useEffect(() => {
    if (isSoldAccount && (!selectedPaymentMethod || selectedPaymentMethod?.toUpperCase() === PaymentMethods.CREDIT)) {
      setSelectedPaymentMethods(PaymentMethods.CARD);
    } else {
      if (!selectedPaymentMethod) {
        if (allowedMethodsForDivision?.includes(PaymentMethods.CREDIT)) {
          setSelectedPaymentMethods(PaymentMethods.CREDIT);
        } else {
          setSelectedPaymentMethods(PaymentMethods.CARD);
        }
      }
    }
  }, [isSoldAccount, selectedPaymentMethod]);

  useEffect(() => {
    if (billingAddress && shippingSameAsBilling) {
      setCurrentShippingAddress(billingAddress);
    }
  }, [shippingSameAsBilling, billingAddress]);

  const getShippingAddressComponent = () => {
    return (
      <>
        <section className="">
          <div className="my-6 flex items-center space-x-2">
            <>
              <Checkbox
                id="shippingSameAsBilling"
                checked={shippingSameAsBilling}
                onCheckedChange={(value) => {
                  if (customerAddresses?.length === 0) {
                    return;
                  }
                  setShippingSameAsBilling(value);
                  if (value === true) {
                    const billingAddressWithCountry = { ...billingAddress, country: 'US' };
                    setCurrentShippingAddress(billingAddressWithCountry);
                  } else {
                    if (customerAddresses?.length > 0) {
                      onAddressChange(customerAddresses?.[0]?.addressId);
                    }
                  }
                }}
              />
              <label
                htmlFor="shippingSameAsBilling"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {mapLocaleToMeaningfulFormat().shippingSameAsBilling}
              </label>
            </>
          </div>
          {!shippingSameAsBilling && customerAddresses?.length != 0 && (
            <CustomFloatingSelect
              label={'shippingAddress'}
              onChange={onAddressChange}
              id={'shippingAddress'}
              register={register}
              setValue={setValue}
              initialState={shippingAddress?.addressId}
              errors={errors}
              classname={'w-[400px] my-3'}
              placeholder={'*Select Address'}
              options={customerAddresses
                ?.filter((item: any) => (shippingSameAsBilling ? true : item?.addressId !== billingAddress?.addressId))
                ?.map((items: any) => (
                  <option key={items?.addressId} value={items?.addressId} className="px-2">
                    <p className="gbmask">
                      {items?.firstName} {items?.lastName}
                    </p>
                  </option>
                ))}
            />
          )}
          <section className="overflow-hidden text-ellipsis pr-24 pt-2.5 text-base font-normal lg:pr-3">
            {shippingAddress && (
              <section className="gbmask">
                <p>
                  {shippingAddress?.firstName} {shippingAddress?.lastName}
                </p>
                <p>
                  {shippingAddress?.streetNumber} {shippingAddress?.streetName}
                </p>
                <p>{shippingAddress?.additionalAddressInfo}</p>
                <p>
                  {shippingAddress?.city}, {shippingAddress?.state} {maskPostalCode(shippingAddress?.postalCode)}
                </p>
              </section>
            )}

            {shippingAddress?.phone && <p className="gbmask mt-3">{maskPhoneNumber(shippingAddress?.phone)}</p>}
            {!shippingSameAsBilling && (
              <button
                type="button"
                className="pt-2 text-blueLight underline"
                onClick={() => setOpenEditAddressModal(true)}
              >
                {mapLocaleToMeaningfulFormat().editAddress}
              </button>
            )}
          </section>

          {openEditAddressModal && (
            <EditAddressModal
              Texts={Texts}
              data={shippingAddress}
              onSuccessfulUpdate={async (addressId: any, data: any) => {
                const completeAddress = { ...data, addressId };
                setCurrentShippingAddress(completeAddress);

                const currentAddressIndex = customerAddresses?.findIndex(
                  (address: any) => address?.addressId === addressId,
                );
                if (currentAddressIndex >= 0) {
                  const updatedAddresses = [...customerAddresses];
                  updatedAddresses[currentAddressIndex] = completeAddress;
                  setCustomerAddresses(updatedAddresses);
                  setCustomerAccountAddresses(updatedAddresses);
                }

                if (isOrderDetails && (addressId === orderShippingAddress?.addressId || shippingSameAsBilling)) {
                  setOrderShippingAddress?.(completeAddress);
                  setOpen?.(false);
                }
              }}
              open={openEditAddressModal}
              setOpen={setOpenEditAddressModal}
              makeAPIcall={true}
              customerId={cartData?.omsCustomerId}
              shippingSameAsBilling={shippingSameAsBilling}
              setCustomerAccountAddresses={setCustomerAccountAddresses}
            />
          )}
        </section>
      </>
    );
  };

  const onAddressChange = async (addressId: string, addresses?: any[]) => {
    setIsLoading(true);
    try {
      const addressList = addresses || customerAddresses;
      const updatedAddress = addressList?.find((address: any) => address?.addressId === addressId);
      if (updatedAddress) {
        const addressWithCountry = {
          ...updatedAddress,
          country: 'US',
        };
        setShippingAddressValue(addressWithCountry);
        setShippingAddress(updatedAddress);
        reset(updatedAddress);
        setLastEnteredShippingAddress(addressWithCountry);
        await trigger();
      }
    } catch (error) {
      console.error('Error in onAddress Change');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (cartData?.cartState === CART_STATE_FROZEN) {
      setIscartHold(true);
    } else {
      setIscartHold(false);
    }
  }, [cartData]);

  return (
    <section className="mx-0 w-full">
      {isOrderDetails ? (
        <>
          <section className="mb-6  px-6">
            <p className="text-lg font-bold">{Texts?.shippingAddress}</p>
            <p className="text-xs">{Texts?.requiredFields}</p>
          </section>

          <section className="px-6">
            <RadioGroup
              defaultValue="comfortable"
              className="my-4 mt-5 flex gap-x-5"
              value={addressType}
              onValueChange={handleAddressTypeChange}
            >
              <section className="flex items-center space-x-2">
                <RadioGroupItem value="newAddress" id="newAddress" onClick={() => setOpenAddAddressModal(true)} />
                <Label htmlFor="newAddress">{mapLocaleToMeaningfulFormat().newAddress}</Label>
              </section>
              <section className="flex items-center space-x-2">
                <RadioGroupItem value="savedAddress" id="savedAddress" />
                <Label htmlFor="savedAddress">{mapLocaleToMeaningfulFormat().savedAddress}</Label>
              </section>
            </RadioGroup>

            {addressType === 'savedAddress' && <>{getShippingAddressComponent()}</>}
          </section>

          {addressType === 'newAddress' && openAddAddressModal && (
            <section className="mb-6 px-6">
              <form>
                <ShippingForm
                  errors={errors}
                  register={register}
                  setValue={setValue}
                  getValues={getValues}
                  clearErrors={clearErrors}
                  onSubmit={() => {}}
                  verifyAddressRef={verifyAddressRef}
                  pageName="checkout-guest"
                  trigger={trigger}
                />
              </form>
            </section>
          )}

          <section className="mt-6 flex gap-4 px-6">
            <Button
              disabled={!shippingAddress}
              className="rounded-md bg-deepBlue p-4 text-white disabled:opacity-50"
              onClick={(event) => callVerifyAddress(event)}
            >
              {addressType === 'newAddress' ? Texts.addNewAddress : Texts.updateAddress}
            </Button>
            <Button
              className="p-0 underline"
              type="reset"
              onClick={() => {
                setShippingSameAsBilling(false);
                setShippingAddress(previousShippingAddress);
                setCurrentShippingAddress(previousShippingAddress);
                if (addressType === 'newAddress') {
                  setOpenAddAddressModal(false);
                  handleAddressTypeChange('savedAddress');
                } else {
                  setOpen?.(false);
                }
              }}
            >
              {Texts.cancelButton}
            </Button>
          </section>
        </>
      ) : (
        <>
          <section className="md:rounded-sm md:border md:border-foggyGray md:shadow-sm md:shadow-foggyGray">
            <section className="mb-6 mt-5 flex flex-row gap-3 border-foggyGray pl-4 md:border-b md:pb-4 md:pl-6">
              <Step1 />
              <p className="mt-1 text-xl font-bold">{Texts.shipping}</p>
            </section>

            <section className="mb-6  px-6">
              <p className="text-lg font-bold">{Texts?.shippingAddress}</p>
              <p className="text-xs">{Texts?.requiredFields}</p>
            </section>

            <section className="px-6">
              <RadioGroup
                defaultValue="comfortable"
                className="my-4 mt-5 flex gap-x-5"
                value={addressType}
                onValueChange={handleAddressTypeChange}
              >
                <section className="flex items-center space-x-2">
                  <RadioGroupItem value="newAddress" id="newAddress" />
                  <Label htmlFor="newAddress">{mapLocaleToMeaningfulFormat().newAddress}</Label>
                </section>
                <section className="flex items-center space-x-2">
                  <RadioGroupItem value="savedAddress" id="savedAddress" />
                  <Label htmlFor="savedAddress">{mapLocaleToMeaningfulFormat().savedAddress}</Label>
                </section>
              </RadioGroup>

              {addressType === 'savedAddress' && (
                <>
                  {getShippingAddressComponent()}
                  <div className="my-3 flex items-center space-x-2">
                    <Checkbox id="terms" onCheckedChange={(e) => setSignatureRequired(e)} />
                    <label
                      htmlFor="signature"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Signature Required
                    </label>
                  </div>
                </>
              )}
            </section>

            {addressType === 'newAddress' && (
              <section className="mb-6 border-b border-foggyGray px-6">
                <form>
                  <ShippingForm
                    errors={errors}
                    register={register}
                    setValue={setValue}
                    getValues={getValues}
                    clearErrors={clearErrors}
                    onSubmit={() => {}}
                    verifyAddressRef={verifyAddressRef}
                    pageName="checkout-guest"
                    trigger={trigger}
                  />
                </form>
              </section>
            )}

            <section className="mb-5 border-foggyGray px-6">
              <ShippingMethod cartData={cartData} shippingMethods={shippingMethods} />
            </section>
            <section className="border-t border-foggyGray px-4 pt-5 lg:border-b-0">
              <button type="button" className="flex items-center gap-x-2 pb-5" onClick={handleShowGiftMessage}>
                {!showGiftMessage ? <AddCircle className="h-5 w-5" /> : <SubtractCircle />}
                <p className="text-base font-normal leading-5">{Texts?.giftMessagePlaceholder}</p>
                <p className="text-sm font-normal leading-5">({Texts?.optionalText})</p>
              </button>

              <section className={`${!showGiftMessage ? 'hidden ' : 'block'} mx-2 mb-6 pt-2`}>
                <FloatingTextArea
                  items={Texts?.giftMessage}
                  classname="w-full"
                  id={Texts?.giftMessageId}
                  htmlFor={Texts?.giftMessageId}
                  value={giftMessage}
                  onChange={handleGiftValue}
                />
              </section>
            </section>
          </section>

          {isLoading && <Loader />}
          <section className="p-4 lg:mt-6 lg:p-0">
            <Button
              disabled={isCartHold}
              onClick={(event) => callVerifyAddress(event)}
              type="button"
              className="w-full rounded-sm bg-deepBlue text-lg text-white lg:w-64"
            >
              {Texts?.continueToPayment}
            </Button>
          </section>
        </>
      )}
    </section>
  );
};

export default GuestShipping;
